<template>
	<div class="card">
		<div class="header">
			<h3>Servicer Details</h3>
		</div>
		<div v-if="loanDetails.loan.productType === 1" class="item">
			<span class="label">Draw Status</span>
			<span class="dots"></span>
			<span class="value-container">
				<span class="value">{{ loanDetails.locStatusHumanized }}</span>
				<PermissionsWrapper :permissionIds="[Permissions.LoanDetails]" :permissionLevel="PermissionLevels.Update">
					<button @click="showLocMetadataModal">Override</button>
				</PermissionsWrapper>
			</span>
		</div>
		<div class="item">
			<span class="label">Repayment Type</span>
			<span class="dots"></span>
			<span class="value">{{ loanDetails.repaymentTypeHumanized }}</span>
		</div>
		<div class="item">
			<span class="label">Repayment Term</span>
			<span class="dots"></span>
			<span class="value">{{ loanDetails.repaymentTermFormatted }}</span>
		</div>
		<div class="item">
			<span class="label">Loan Stage</span>
			<span class="dots"></span>
			<span class="value">{{ loanDetails.loanStageHumanized }}</span>
		</div>
		<div class="item">
			<span class="label">Servicer Name</span>
			<span class="dots"></span>
			<a v-if="adminLoginUrl" :href="adminLoginUrl" class="value" target="_blank">
				{{ servicerName }}
			</a>
			<span v-else class="value">
				{{ servicerName }}
			</span>
		</div>
		<div class="item">
			<span class="label">Id at Servicer</span>
			<span class="dots"></span>
			<span class="value">{{ loanDetails.servicerLoanData?.servicerLoanId || 'N/A' }}</span>
		</div>
	</div>

	<LocMetadataForm
		v-if="locMetadataFormVisible"
		:isSubmitting="isSubmitting"
		:fields="formFields"
		:validationSchema="validationSchema"
		@submit="updateLocMetadataAsync"
		@close="closeLocMetadataModal"
	/>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import PermissionsWrapper from '@/components/wrappers/PermissionsWrapper.vue';
	import { Permissions, PermissionLevels } from '@/models/permissions';
	import { LoanDetails, LocMetadata } from '@/models/loans';
	import LocMetadataForm from './LocMetadataForm.vue';
	import { useUpdateLocMetadataFormFields } from '@/composables/loan/useUpdateLocMetadataForm';

	const props = defineProps<{
		loanDetails: LoanDetails;
		onUpdateLoan: () => Promise<void>;
	}>();

	const isSubmitting = ref<boolean>(false);
	const locMetadataFormVisible = ref<boolean>(false);

	const locMetadata = computed<LocMetadata | null>(() => props.loanDetails.locMetadata);
	const servicerName = computed(() => props.loanDetails.servicerPartner?.name || 'N/A');
	const adminLoginUrl = computed(() => props.loanDetails.servicerPartner?.adminLoginUrl);

	const { formFields, validationSchema } = useUpdateLocMetadataFormFields({
		locMetadata
	});

	const showLocMetadataModal = () => (locMetadataFormVisible.value = true);
	const closeLocMetadataModal = () => (locMetadataFormVisible.value = false);

	async function updateLocMetadataAsync(formData: Record<string, any>) {
		isSubmitting.value = true;

		const loanId = props.loanDetails.loan.id;
		const referenceId = props.loanDetails.loan.referenceId;

		try {
			// sanitize override amount
			formData.drawOverrideAmount = String(formData.drawOverrideAmount).replace(/[$,]/g, '');

			const request = {
				loanId,
				...formData
			};

			await axios.patch(`/api/loanservice/loans/${loanId}/locmetadata`, request);

			await props.onUpdateLoan();

			closeLocMetadataModal();

			functions.openModal({
				title: 'Success!',
				description: `Successfully updated the loc override for loan ${referenceId}!`
			});
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: `There was an issue updating the loc override for loan ${referenceId}. Please try again later.`
			});
		} finally {
			isSubmitting.value = false;
		}
	}
</script>

<style scoped>
	a {
		position: relative;
		font-style: italic;
		color: #333;
		text-decoration: none;
	}

	a::after {
		content: '';
		position: absolute;
		left: 0;
		bottom: -2px;
		width: 0;
		height: 2px;
		background-color: #333;
		transition: width 0.1s ease;
	}

	a:hover::after {
		width: 100%;
	}

	.value-container {
		display: flex;
		align-items: flex-end;
		gap: 6px;
	}

	button {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 4px 8px;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-family: 'Montserrat', Arial, sans-serif;
		transition: background-color 0.2s, color 0.2s;
		font-size: 14px;
		color: #777;
		background-color: white;
		box-shadow: 0 0 0 1px #ccc;
	}

	button:hover {
		background-color: black;
		color: white;
		border: none;
		box-shadow: 0 0 0 1px black;
	}
</style>
